import { useConfirm } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';

import { ORDER_ERRORS } from './constants';
import { useCancelOrder, useUpdateOrderStatus } from './hooks';
import type { OrderDetail, OrderStatus } from './types';

const errorTitle = (e: unknown) =>
  e instanceof Error ? e.message : 'Something went wrong. Please try again.';

/**
 * Shared status/cancel actions for the detail page — used by both the status dropdown and the
 * More Actions menu (Cancel Order). Each action runs its own confirmation dialog and toast.
 */
export function useOrderStatusActions(order: OrderDetail) {
  const { confirm } = useConfirm();
  const toast = useToast();
  const updateStatus = useUpdateOrderStatus(order.id);
  const cancel = useCancelOrder(order.id);

  const cancelOrder = async () => {
    // Mutation runs inside onConfirm so the dialog's "Cancel Order & Refund" button shows a
    // loading spinner for the duration of the (latency-simulated) API call.
    await confirm({
      title: 'Cancel this Order ?',
      subtitle: 'Are you sure you want to cancel this order? Once done refund will be initiated.',
      confirmText: 'Cancel Order & Refund',
      cancelText: 'Keep Order',
      danger: true,
      onConfirm: async () => {
        try {
          await cancel.mutateAsync();
          toast.success({ title: 'Order cancelled successfully. Refund has been initiated.' });
        } catch (e) {
          toast.error({ title: errorTitle(e) });
        }
      },
    });
  };

  const changeStatus = async (next: OrderStatus) => {
    if (next === 'cancelled') return cancelOrder();
    // FE guard mirrors the server: Delivered is blocked while an installation request is pending.
    if (next === 'delivered' && order.installationRequest?.status === 'pending') {
      toast.error({ title: ORDER_ERRORS.installationPending });
      return;
    }
    await confirm({
      title: 'Change order status?',
      subtitle: 'The new order status will be visible to the customer in the webstore.',
      confirmText: 'Change Status',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await updateStatus.mutateAsync(next);
          toast.success({ title: 'Order status updated successfully.' });
        } catch (e) {
          toast.error({ title: errorTitle(e) });
        }
      },
    });
  };

  return { changeStatus, cancelOrder, isPending: updateStatus.isPending || cancel.isPending };
}
